import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { getLoggedInUser } from "../utils/auth";

export default function EmployeePickupPage() {
  const user = getLoggedInUser() || {};
  const employeeCode =
    user.userId || user.UserID || user.username || user.UserName || "";

  const role = user?.role || user?.Role || "";
  const ROLE_TEXT =
    role === "SupervisorCumEmployee" ? "SUPERVISOR + EMPLOYEE" : "EMPLOYEE";

  const [employeeName, setEmployeeName] = useState("");
  const [tasks, setTasks] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [scannedLocation, setScannedLocation] = useState({});
  const [pickQty, setPickQty] = useState({});

  const [cardInput, setCardInput] = useState("");
  const [cardScanned, setCardScanned] = useState(false);

  useEffect(() => {
    if (employeeCode) {
      loadEmployee();
      loadTasks();
    }
  }, [employeeCode]);

  const loadEmployee = async () => {
    try {
      const res = await api.get(`/api/pickup/user/${employeeCode}`);
      setEmployeeName(res.data?.empName || "");
    } catch (err) {
      console.error(err);
    }
  };

  const loadTasks = async () => {
    try {
      const res = await api.get(`/api/pickup/tasks/${employeeCode}`);
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setTasks([]);
    }
  };

  // ================= ORDER SUMMARY =================
  const uniqueOrders = useMemo(() => {
    const orderIds = [...new Set(tasks.map((t) => t.OrderID))];
    return orderIds.map((orderId) => {
      const related = tasks.filter((t) => t.OrderID === orderId);
      return {
        orderId,
        totalSkus: related.length,
        totalRequested: related.reduce(
          (sum, t) => sum + Number(t.RequestedPieces || 0),
          0
        ),
        totalPicked: related.reduce(
          (sum, t) => sum + Number(t.PickedPieces || 0),
          0
        ),
      };
    });
  }, [tasks]);

  const currentOrderTasks = useMemo(() => {
    return selectedOrderId
      ? tasks.filter((t) => t.OrderID === selectedOrderId)
      : [];
  }, [tasks, selectedOrderId]);

const isOrderFullyPicked = useMemo(() => {
  if (!selectedOrderId) return false;

  const orderTasks = tasks.filter((t) => t.OrderID === selectedOrderId);

  // 🚨 CRITICAL FIX
  if (orderTasks.length === 0) return true;

  return orderTasks.every(
    (t) =>
      Number(t.PickedPieces || 0) >=
      Number(t.RequestedPieces || 0)
  );
}, [tasks, selectedOrderId]);

  // ================= LOG PICK =================
  const handleLogPick = async (task) => {
    const key = `${task.TaskID}_${task.LocationID}_${task.ASN}_${task.OrderID}`;

    const qty = Number(pickQty[key] || 0);
    const scanned = scannedLocation[key];

    if (!scanned || !scanned.trim()) {
      alert("Please scan location first.");
      return;
    }

    if (scanned.trim() !== String(task.LocationID || "").trim()) {
      alert("Invalid location scanned.");
      return;
    }

    if (!qty || qty <= 0) {
      alert("Enter a valid pick quantity.");
      return;
    }

    const remaining =
      Number(task.RequestedPieces || 0) -
      Number(task.PickedPieces || 0);

    if (qty > remaining) {
      alert(`Pick qty cannot exceed remaining (${remaining}).`);
      return;
    }

    try {
      await api.post("/api/pickup/tasks/log-pick", {
        taskId: task.TaskID,
        orderId: task.OrderID,
        employeeId: employeeCode,
        sku: task.SKU,
        asn: task.ASN,
        locationId: task.LocationID,
        qty,
      });

      setTasks((prev) =>
        prev.map((t) => {
          if (
            t.TaskID === task.TaskID &&
            t.LocationID === task.LocationID &&
            t.ASN === task.ASN &&
            t.OrderID === task.OrderID
          ) {
            const newPicked =
              Number(t.PickedPieces || 0) + qty;

            return {
              ...t,
              PickedPieces: newPicked,
              Status:
                newPicked >= Number(t.RequestedPieces)
                  ? "Completed"
                  : "InProgress",
            };
          }
          return t;
        })
      );

      alert("Pick logged successfully.");

      setPickQty((prev) => ({ ...prev, [key]: "" }));
      setScannedLocation((prev) => ({ ...prev, [key]: "" }));

      await loadTasks();

    } catch (err) {
      console.error(err);
      alert("Error logging pick");
    }
  };

  // ================= CARD SCAN =================
  const handleCardScan = async (value) => {
    const scanned = String(value).trim();

    if (scanned !== String(employeeCode).trim()) {
      alert("Invalid card scanned.");
      setCardScanned(false);
      setCardInput("");
      return;
    }

    try {
      await api.post("/api/pickup/orders/card-scan", {
        employeeId: employeeCode,
        orderId: selectedOrderId,
      });

      setCardScanned(true);
      alert("Card verified.");
    } catch (err) {
      console.error(err);
      alert("Card scan failed.");
      setCardScanned(false);
    }
  };

  // ================= COMPLETE ORDER =================
  const handleCompleteOrder = async () => {
    try {
      await api.post("/api/pickup/orders/complete", {
        orderId: selectedOrderId,
        employeeId: employeeCode,
      });

      alert("Order completed successfully!");

      setSelectedOrderId("");
      setCardScanned(false);
      setCardInput("");
      setPickQty({});
      setScannedLocation({});

      await loadTasks();
    } catch (err) {
      console.error(err);
      alert("Error completing order.");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Order Pickup</h2>

        {/* ORDER LIST */}
        <div style={styles.sectionTitle}>Assigned Orders</div>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Select</th>
                <th>Order ID</th>
                <th>SKUs</th>
                <th>Requested</th>
                <th>Picked</th>
              </tr>
            </thead>
            <tbody>
              {uniqueOrders.map((o) => (
                <tr
                  key={o.orderId}
                  style={selectedOrderId === o.orderId ? styles.rowSelected : {}}
                >
                  <td>
                    <input
                      type="radio"
                      checked={selectedOrderId === o.orderId}
                      onChange={() => setSelectedOrderId(o.orderId)}
                    />
                  </td>
                  <td>{o.orderId}</td>
                  <td>{o.totalSkus}</td>
                  <td>{o.totalRequested}</td>
                  <td>{o.totalPicked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* TASK TABLE */}
        {selectedOrderId && (
          <>
            <div style={styles.sectionTitle}>
              SKU Tasks for Order: {selectedOrderId}
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>TaskID</th>
                    <th>SKU</th>
                    <th>ASN</th>
                    <th>Location</th>
                    <th>Requested</th>
                    <th>Picked</th>
                    <th>Status</th>
                    <th>Scan Location</th>
                    <th>Pick Qty</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentOrderTasks.map((t) => {
                    const key = `${t.TaskID}_${t.LocationID}_${t.ASN}_${t.OrderID}`;
                    const isDone =
                      Number(t.PickedPieces || 0) >=
                      Number(t.RequestedPieces || 0);

                    return (
                      <tr key={key} style={isDone ? styles.rowCompleted : {}}>
                        <td>{t.TaskID}</td>
                        <td>{t.SKU}</td>
                        <td>{t.ASN}</td>
                        <td>{t.LocationID}</td>
                        <td>{t.RequestedPieces}</td>
                        <td>{t.PickedPieces}</td>
                        <td style={{ fontWeight: "bold" }}>
                          {isDone ? "Completed" : t.Status || "Assigned"}
                        </td>

                        <td>
                          <input
                            placeholder="Scan Location"
                            style={{ width: 120 }}
                            disabled={isDone}
                            value={scannedLocation[key] || ""}
                            onChange={(e) =>
                              setScannedLocation((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            style={{ width: 70 }}
                            disabled={isDone}
                            value={pickQty[key] || ""}
                            onChange={(e) =>
                              setPickQty((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                          />
                        </td>

                        <td>
                          <button
                            style={styles.smallBtn}
                            disabled={isDone}
                            onClick={() => handleLogPick(t)}
                          >
                            Log Pick
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* CARD SCAN */}
            {isOrderFullyPicked && (
              <div style={{ marginTop: 20 }}>
                <p style={{ color: "green", fontWeight: "bold" }}>
                  All picks completed! Scan your card to complete order.
                </p>

                <input
                  style={{
                    width: "60%",
                    padding: 10,
                    borderRadius: 8,
                    border: "1px solid #aaa",
                  }}
                  placeholder="Scan your ID card"
                  value={cardInput}
                  onChange={(e) => setCardInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCardScan(cardInput);
                    }
                  }}
                />

                {cardScanned ? (
                  <p style={{ color: "green" }}>✔ Card verified</p>
                ) : (
                  <p style={{ color: "red" }}>❗ Scan required</p>
                )}

                <button
                  style={{
                    marginTop: 15,
                    padding: 12,
                    width: "50%",
                    borderRadius: 8,
                    border: "none",
                    background:
                      isOrderFullyPicked && cardScanned ? "#009944" : "#888",
                    color: "#fff",
                    cursor:
                      isOrderFullyPicked && cardScanned
                        ? "pointer"
                        : "not-allowed",
                  }}
                  disabled={!isOrderFullyPicked || !cardScanned}
                  onClick={handleCompleteOrder}
                >
                  Complete Order
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- STYLES ----------------
const styles = {
  page: { background: "#f5f6fa", minHeight: "100vh", padding: 20 },
  card: {
    maxWidth: 1000,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
  },
  heading: { textAlign: "center", color: "#00358f", marginBottom: 20 },
  sectionTitle: {
    marginTop: 18,
    marginBottom: 8,
    fontWeight: "bold",
    borderBottom: "1px solid #ddd",
    paddingBottom: 4,
  },
  tableWrapper: {
    maxHeight: 260,
    overflowY: "auto",
    border: "1px solid #ddd",
    borderRadius: 8,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  rowSelected: { background: "#e5f0ff" },
  rowCompleted: { background: "#f0fff0" },
  smallBtn: {
    padding: "4px 10px",
    fontSize: 12,
    borderRadius: 6,
    border: "none",
    background: "#00358f",
    color: "#fff",
    cursor: "pointer",
  },
};